import { useState, useEffect } from "react";
import { Button } from "@vibe/core";
import mondaySdk from "monday-sdk-js";
import useConsent from "../../hooks/api/useConsent";
import { canSubmit } from "../../utils/consentModel";
import { CONSENT_SOURCE_ONBOARDING } from "../../data/constants/legalPolicies";
import ConsentCheckboxes from "./ConsentCheckboxes";
import type { ConsentSelections } from "../../utils/consentTypes";

const monday = mondaySdk();

interface LegalConsentFormProps {
  /** Called after consent is successfully recorded. */
  onComplete: () => void;
  /** Capture surface for the audit log. Defaults to onboarding. */
  source?: string;
  /** Override the submit button label. */
  submitLabel?: string;
}

const LegalConsentForm = ({
  onComplete,
  source = CONSENT_SOURCE_ONBOARDING,
  submitLabel = "I agree & continue",
}: LegalConsentFormProps) => {
  const { consentState, submitConsent, submitting } = useConsent();

  const [sel, setSel] = useState<ConsentSelections>({ terms: false, privacy: false, marketing: false });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pre-check boxes from any prior decision once status resolves.
  useEffect(() => {
    setSel({
      terms: consentState.termsGranted,
      privacy: consentState.privacyGranted,
      marketing: consentState.marketingGranted,
    });
  }, [consentState.termsGranted, consentState.privacyGranted, consentState.marketingGranted]);

  const handleSubmit = async () => {
    setErrorMsg(null);
    try {
      await submitConsent(sel, source);
      onComplete();
    } catch {
      setErrorMsg("We couldn't record your consent. Please try again.");
      monday.execute("notice", {
        message: "Failed to record your consent. Please try again.",
        type: "error",
        timeout: 5000,
      });
    }
  };

  return (
    <div>
      <ConsentCheckboxes value={sel} onChange={setSel} />

      {errorMsg && (
        <p className="!font-figtree mt-3 text-xs text-red-500 text-center" role="alert">
          {errorMsg}
        </p>
      )}

      <div className="consent-form-actions flex justify-center mt-6">
        <Button
          color="primary"
          disabled={!canSubmit(sel) || submitting}
          loading={submitting}
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default LegalConsentForm;
